/**
 * The month view.
 *
 * ===========================================================================
 * WHAT IS DELIBERATELY NOT ON THIS GRID
 * ===========================================================================
 * CRITICAL DATES. They are a risk register, not a diary. A limitation date drawn as a
 * square among meetings invites the reading that missing it is like missing a meeting,
 * and it is not — it is a potential negligence exposure, it requires independent
 * verification, it cannot be moved or deleted, and it survives the matter closing.
 * They live on their own screen, which this one links to and does not duplicate.
 * ===========================================================================
 *
 * A day cell shows at most three entries and then says how many more there are, rather
 * than scrolling inside a square. The count is a real number, not "…": somebody
 * scanning for a clash needs to know a day is busy even when they cannot see what with.
 */

import type { CalendarEvent } from '@legalcrm/api';
import { cn } from '@legalcrm/ui';

import { eventTone, timeLabel, weekdayLabels, type GridDay } from './calendarMonth';

const MAX_PER_DAY = 3;

const dotTones: Record<ReturnType<typeof eventTone>, string> = {
  danger: 'bg-danger',
  primary: 'bg-primary',
  info: 'bg-info',
  neutral: 'bg-ink-subtle',
};

export interface MonthGridProps {
  days: GridDay[];
  /** Local day key (`YYYY-MM-DD`) to the events starting on it, already sorted. */
  eventsByDay: Map<string, CalendarEvent[]>;
  onSelect: (event: CalendarEvent) => void;
  onSelectDay: (day: GridDay) => void;
}

export function MonthGrid({ days, eventsByDay, onSelect, onSelectDay }: MonthGridProps) {
  const labels = weekdayLabels();

  return (
    <div className="overflow-hidden rounded-lg border bg-surface shadow-sm">
      <div className="grid grid-cols-7 border-b bg-surface-sunken">
        {labels.map((label) => (
          <div
            key={label}
            className="px-2 py-1.5 text-center text-xs font-semibold uppercase tracking-wide text-ink-muted"
          >
            {label}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {days.map((day) => {
          const events = eventsByDay.get(day.key) ?? [];
          const overflow = events.length - MAX_PER_DAY;

          return (
            <div
              key={day.key}
              className={cn(
                'min-h-[6.5rem] border-b border-r p-1.5 last:border-r-0',
                !day.inMonth && 'bg-surface-sunken/50',
                day.isWeekend && day.inMonth && 'bg-surface-sunken/30',
              )}
            >
              <button
                type="button"
                onClick={() => onSelectDay(day)}
                className={cn(
                  'mb-1 flex h-6 w-6 items-center justify-center rounded-full text-xs tabular-nums',
                  day.isToday
                    ? 'bg-primary font-semibold text-primary-ink'
                    : day.inMonth
                      ? 'text-ink hover:bg-surface-sunken'
                      : 'text-ink-subtle hover:bg-surface-sunken',
                )}
                aria-label={`Add an entry on ${day.date.toDateString()}`}
              >
                {day.date.getDate()}
              </button>

              <ul className="flex flex-col gap-0.5">
                {events.slice(0, MAX_PER_DAY).map((event) => (
                  <li key={event.id}>
                    <button
                      type="button"
                      onClick={() => onSelect(event)}
                      className="flex w-full items-center gap-1 rounded px-1 py-0.5 text-left text-xs hover:bg-surface-sunken"
                      title={event.title}
                    >
                      <span
                        className={cn(
                          'h-1.5 w-1.5 shrink-0 rounded-full',
                          dotTones[eventTone(event.event_type)],
                        )}
                        aria-hidden="true"
                      />
                      <span className="shrink-0 tabular-nums text-ink-muted">
                        {timeLabel(event.starts_at, event.all_day)}
                      </span>
                      <span
                        className={cn(
                          'truncate',
                          event.status === 'cancelled'
                            ? 'text-ink-subtle line-through'
                            : 'text-ink',
                        )}
                      >
                        {event.title}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>

              {overflow > 0 && (
                <p className="px-1 pt-0.5 text-xs text-ink-muted">
                  {overflow} more — open the list
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
